import { createApi } from '@reduxjs/toolkit/query/react'
import { HYDRATE } from 'next-redux-wrapper'
import { axiosBaseQuery } from '../../lib/axios'

function isHydrateAction(action) {
	return action.type === HYDRATE
}

function normalizeGuestPricing(pricing) {
	if (!pricing) {
		return null
	}

	return {
		title: pricing.title,
		interval: pricing.interval || '',
		cost: Number(pricing.cost || 0),
		iva: Number(pricing.iva || 0),
		price: Number(pricing.price || pricing.cost || 0),
		discount: Number(pricing.discount || 0),
		currency: pricing.currency || '€',
	}
}

function normalizeGuestPromo(promo, promoCode = null) {
	return {
		code: promo?.code ?? promoCode ?? null,
		applied: Boolean(promo?.applied),
		error: promo?.error || null,
	}
}

function normalizeGuestPricingResponse(response, arg) {
	const pricing = response?.pricing
		? normalizeGuestPricing(response.pricing)
		: resolvePricingFromServices(response?.services, arg?.serviceId || arg?.keyword)

	return {
		pricing,
		promo: normalizeGuestPromo(response?.promo, arg?.promoCode),
	}
}

function resolvePricingFromServices(services = [], serviceId) {
	if (!Array.isArray(services) || services.length === 0) {
		return null
	}

	const matchedService = services.find((service) => {
		if (!serviceId) {
			return true
		}

		return (
			String(service.id) === String(serviceId) ||
			service.slug === serviceId ||
			service.keyword === serviceId ||
			service.title === serviceId
		)
	})

	if (!matchedService) {
		return null
	}

	return normalizeGuestPricing({
		title: matchedService.title,
		interval: matchedService.interval,
		cost: matchedService.cost,
		iva: matchedService.iva,
		price: matchedService.price || matchedService.cost,
		discount: matchedService.discount,
		currency: matchedService.currency,
	})
}

export const PaymentsApi = createApi({
	reducerPath: 'PaymentsApi',
	baseQuery: axiosBaseQuery(),
	extractRehydrationInfo(action, { reducerPath }) {
		if (isHydrateAction(action)) {
			return action.payload[reducerPath]
		}
	},
	endpoints: (builder) => ({
		getServicePricingForGuest: builder.query({
			query: ({ serviceSlug, promoCode }) => {
				return {
					url: `v1/guest/${serviceSlug}/price`,
					method: 'GET',
					params: promoCode
						? {
							promo_code: promoCode,
						}
						: undefined,
				}
			},
			transformResponse: (response, meta, arg) => {
				return normalizeGuestPricingResponse(response, arg)
			},
		}),
		checkEmailExists: builder.mutation({
			query: ({ email }) => ({
				url: 'user/email/exists',
				method: 'POST',
				data: { email },
				headers: {
					'Content-Type': 'application/json',
				},
			}),
		}),
		requestLoginOtp: builder.mutation({
			query: ({ email, channel }) => ({
				url: 'user/login/otp/request',
				method: 'POST',
				data: { email, channel },
				headers: {
					'Content-Type': 'application/json',
				},
			}),
		}),
		verifyLoginOtp: builder.mutation({
			query: ({ email, channel, otp }) => ({
				url: 'user/login/otp/verify',
				method: 'POST',
				data: { email, channel, otp },
				headers: {
					'Content-Type': 'application/json',
				},
			}),
		}),
		autoRegisterNewUser: builder.mutation({
			query: ({ name, lname, email, mobile_number_code, mobile_number }) => ({
				url: 'user/register/auto',
				method: 'POST',
				data: { name, lname, email, mobile_number_code, mobile_number },
				headers: {
					'Content-Type': 'application/json',
				},
			}),
		}),

	}),
})

export const {
	useGetServicePricingForGuestQuery,
	useCheckEmailExistsMutation,
	useRequestLoginOtpMutation,
	useVerifyLoginOtpMutation,
	useAutoRegisterNewUserMutation,
} = PaymentsApi

export const {
	getServicePricingForGuest,
	checkEmailExists,
	requestLoginOtp,
	verifyLoginOtp,
	autoRegisterNewUser,
} = PaymentsApi.endpoints
